import { isString, get, omitBy, isNil, map } from 'lodash-es';
import moment from 'moment';
import type { CoreTypes } from '../../core';
import type { Activity } from '../types/activities';
import type { ActivityType } from '../types/codes';
import type { Duty } from '../types/duties';
import { getDivision, getDivisionId, hasDivisionId, toReference, type DivisionScope } from './common';
import toGroup from './groups';
import toTeacher from './duties';

type Expand = ('referenceNames' | 'teachers' | 'syllabus' | 'groups')[];

function mapActivityType (type: CoreTypes.Deep.Course['type']): ActivityType {
  switch (type) {
    case 'LUNCH':
      return 'Övrigt';
    default:
      return 'Undervisning';
  }
}

/**
 * @deprecated This is only used for SCS. can be removed when scs use v2
 */
export default (
  activities: (Extract<CoreTypes.Mixed.Course, { id: string }> | Extract<CoreTypes.Mixed.Event, { id: string }>)[],
  domain?: DivisionScope,
  expand?:    Expand
): Activity[] => {
  if (!Array.isArray(activities))
    throw new Error('(SS12000::To::Activities) Activities input is not of array type', { cause: 'invalid_input' });

  if (!activities.every((activity) => isString(activity) || isString(get(activity, 'id'))))
    throw new Error('(SS12000::To::Activities) Some Activities are missing "id" values', { cause: 'invalid_input' });

  if (!domain && !activities.every((activity) => !isString(activity) && hasDivisionId(activity)))
    throw new Error('(SS12000::To::Activities) Some top layer activities are missing division values', { cause: 'invalid_input' });

  return activities.map((activity): Activity => {
    const division = getDivision(activity, domain);
    if (!division || isString(division) || !('id' in division && division.id))
      throw new Error('(SS12000::To::Activities) Some events members are missing division values', { cause: 'invalid_input' });
    if (!('start' in division && division.start) || !('end' in division && division.end))
      throw new Error('(SS12000::To::Activities) Some events members are missing division start or end values', { cause: 'invalid_input' });

    return {
      id:                     `${ getDivisionId(activity, domain) }.${ isString(activity) ? activity : activity.id }`,
      calendarEventsRequired: 'course' in activity, // true for events, false for courses
      activityType:           mapActivityType(activity.type),
      startDate:              moment(division.start).toISOString(),
      ...typeof activity != 'string' && {
        meta: {
          created:  moment(activity.createdAt).toISOString(),
          modified: moment(activity.updatedAt).toISOString()
        },
        ...omitBy({
          foreignId:   activity.ids,
          displayName: activity.displayName,
          comment:     get(activity, 'comment'),
          subject:     get(activity, 'subject'),
        }, isNil),
        // @ts-ignore
        ...activity.groups      && { groups:        toReference(map(activity.groups, 'to'), division, expand?.includes('referenceNames')) },
        // @ts-ignore
        ...activity.teachers    && { teachers: activity.teachers.map(({ to }) => ({ duty: toReference([to], division, expand?.includes('referenceNames'))[0] as Duty })) },
        ...'course' in activity && activity.course && typeof activity.course == 'object' && 'id' in activity.course && activity.course.id && {
        // @ts-ignore
          parentActivity: toReference([activity.course as CoreTypes.Mixed.Course], division, expand?.includes('referenceNames'))[0]
        },
        ...activity.locations  && { rooms: activity.locations.map(({ locations, groupIndex }) => ({
        // @ts-ignore
          room:  toReference(locations as any, division, expand?.includes('referenceNames'))[0],
          index: groupIndex
        }))
        },
        // syllabus: {
        //   id: activity.id!,
        //   ...expandReferenceNames && { displayName: activity.displayName }
        // },
        ...expand?.some(x => ['groups', 'teachers', 'syllabus'].includes(x)) && {
          _embedded: {
            ...expand.includes('groups') && {
            // @ts-ignore
              ...activity.groups && { groups: toGroup(map(activity.groups, 'to'), division) }
            },
            ...expand.includes('teachers') && {
            // @ts-ignore
              ...activity.teachers && { teachers: toTeacher(map(activity.teachers, 'to'), division) }
            },
            // ...expand.includes('syllabus') && {
            //   syllabus: {
            //     id:          activity.id!,
            //     displayName: activity.displayName,
            //   }
            // },
          }
        }
      }
    }
  });
};